import { ErrorMessage as HookformErrorMessage } from '@hookform/error-message';
import { dequal as deepEqual } from 'dequal/lite';
import lodashGet from 'lodash/fp/get';
import { useFormContext, useWatch, type Path } from 'react-hook-form';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { TagLink } from '#components/patterns/entity-links/tag-link/TagLink.tsx';
import type { ConfigOverride, ConfigOverrideKey } from '#dn-types/host.ts';
import { ResetButton } from '#screens/hosts/form/advanced/reset-button/ResetButton.tsx';
import { checkIsValueFromTag } from '#screens/hosts/form/checkIsValueFromTag.ts';
import type { OverridePlusSource } from '../../../hooks/useOverrideMaps.ts';
import type { HostFormData } from '../../host-form-schema.ts';
import { mapOverridesToForm } from '../mappers/mapOverridesToForm.ts';
import { ResetFieldButton } from '../reset-button/ResetFieldButton.tsx';

type Props = {
  name: Path<HostFormData>;
  overrideMap: Map<string, OverridePlusSource>;
  /** Overrides that are set on the host itself */
  hostOverrides: ConfigOverride[];
  /** Used to clear a host override that matches a tag value */
  clearOverride: (key: ConfigOverrideKey) => void;
};

export function FieldFooter({ name, overrideMap, hostOverrides, clearOverride }: Props) {
  const context = useFormContext<HostFormData>();
  const {
    control,
    formState: { dirtyFields, errors },
    resetField,
  } = context;
  const formValue = useWatch({ control, name });
  const { tagName, isValueFromTag, overrideKey } = checkIsValueFromTag({ name, formValue, overrideMap });

  const mappedOverrides = mapOverridesToForm(hostOverrides);

  // Host overrides can be manually cleared/unset if they match a value provided by a tag
  const canClearOverride =
    tagName && isValueFromTag && overrideKey && deepEqual(lodashGet(overrideKey, mappedOverrides), formValue);

  return (
    <>
      <HookformErrorMessage errors={errors} name={name} as={ErrorMessage} className="mb-8" />
      {
        // Check if the input value and the tag's override value match
        tagName && isValueFromTag ?
          // Allow clearing the override, even if it already matches the tag's value
          overrideKey && canClearOverride ?
            <ResetButton
              onClick={() => {
                clearOverride(overrideKey);
              }}
              isShown={true}
              tagName={tagName}
            />
            // Show "inherited from tag:name".
          : <span className="p-md">
              Inherited from <TagLink className="a" tagName={tagName} />
            </span>

        : <ResetFieldButton name={name} reset={resetField} dirtyFields={dirtyFields} tagName={tagName} />
      }
    </>
  );
}
